"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MESES_ES = exports.FechaPrecisionResponseSchema = exports.FechaPrecisionInputSchema = exports.FechaPrecisionSchema = exports.FECHA_PRECISIONES = void 0;
exports.normalizarFechaPorPrecision = normalizarFechaPorPrecision;
exports.rangoFechasValido = rangoFechasValido;
exports.formatearFechaNumerica = formatearFechaNumerica;
exports.formatearFechaTexto = formatearFechaTexto;
exports.parcheFechaInicio = parcheFechaInicio;
exports.fechaLocalISO = fechaLocalISO;
exports.fechaTerminoMaxima = fechaTerminoMaxima;
exports.erroresLimiteFechas = erroresLimiteFechas;
const zod_1 = require("zod");
// ─── Precisión de las fechas del CV (R64) ────────────────────
// Una fecha del CV puede ser exacta ('dia') o conocerse solo por mes y año ('mes').
// La fecha se sigue guardando como DATE; con 'mes' se guarda el día 1 y ese día NO
// es dato: ninguna vista debe mostrarlo. Helpers puros, compartidos por API y web.
// ──────────────────────────────────────────────────────────────
exports.FECHA_PRECISIONES = ['dia', 'mes'];
exports.FechaPrecisionSchema = zod_1.z.enum(exports.FECHA_PRECISIONES);
/**
 * Precisión en un payload de entrada: opcional y SIN default. La ausencia se resuelve en
 * el service — 'dia' al crear, y al editar se conserva la que ya tenía la fila —, así un
 * cliente viejo (bundle cacheado durante un deploy) no pisa un 'mes' con 'dia'.
 */
exports.FechaPrecisionInputSchema = exports.FechaPrecisionSchema.optional();
/** Precisión en una respuesta: tolera respuestas cacheadas anteriores al campo. */
exports.FechaPrecisionResponseSchema = exports.FechaPrecisionSchema.optional().default('dia');
/** 'YYYY-MM-DD' → con precisión 'mes' fija el día en 01 (lo que se guarda). */
function normalizarFechaPorPrecision(fecha, precision) {
    if (!fecha)
        return null;
    return precision === 'mes' ? `${fecha.slice(0, 7)}-01` : fecha;
}
/**
 * ¿inicio ≤ término? Si cualquiera de las dos es 'mes' la comparación es a nivel de mes:
 * "marzo 2020" → "15 de marzo 2020" es un rango válido aunque el día 1 guardado no diga nada.
 * Sin alguna de las dos fechas no hay rango que validar.
 */
function rangoFechasValido(data) {
    const { fechaInicio, fechaTermino } = data;
    if (!fechaInicio || !fechaTermino)
        return true;
    const porMes = data.fechaInicioPrecision === 'mes' || data.fechaTerminoPrecision === 'mes';
    return porMes ? fechaInicio.slice(0, 7) <= fechaTermino.slice(0, 7) : fechaInicio <= fechaTermino;
}
exports.MESES_ES = [
    'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];
/**
 * Formato corto numérico de una fecha 'YYYY-MM-DD' según su precisión:
 * 'dia' → 'DD-MM-YYYY'; 'mes' → 'MM-YYYY' (el día guardado nunca se muestra).
 */
function formatearFechaNumerica(fecha, precision) {
    if (!fecha)
        return '';
    const [year, month, day] = fecha.slice(0, 10).split('-');
    if (!year || !month || !day)
        return fecha;
    return precision === 'mes' ? `${month}-${year}` : `${day}-${month}-${year}`;
}
/**
 * Formato en palabras: 'dia' → '15 de marzo de 2020'; 'mes' → 'marzo de 2020'.
 */
function formatearFechaTexto(fecha, precision) {
    if (!fecha)
        return '';
    const [year, month, day] = fecha.slice(0, 10).split('-');
    const mes = exports.MESES_ES[Number(month) - 1];
    if (!year || !mes || !day)
        return fecha;
    return precision === 'mes' ? `${mes} de ${year}` : `${Number(day)} de ${mes} de ${year}`;
}
/**
 * Parche del draft al cambiar la fecha de INICIO en un modal del CV, con el autocompletado
 * del término. Regla (una sola para las 5 secciones):
 * - Cambiar SOLO la precisión (el selector "Mes y año · Fecha exacta") nunca toca el término,
 *   aunque normalice el valor de inicio (día → 01).
 * - El término se autocompleta con el inicio (valor Y precisión) solo si cambió el VALOR del
 *   inicio, el ítem no está "en curso" y el término está vacío o todavía es igual al inicio
 *   anterior (lo puso este mismo autocompletado). Tipear la fecha con el teclado emite años
 *   parciales (0002-03-15, 0020-03-15…): el término tiene que seguir al inicio hasta el final.
 * - Un término que el usuario eligió (distinto del inicio anterior) no se pisa nunca.
 */
function parcheFechaInicio(draft, fechaInicio, precision, soloPrecision = false) {
    const patch = { fechaInicio, fechaInicioPrecision: precision };
    const valorCambio = fechaInicio !== (draft.fechaInicio ?? '');
    const terminoLibre = !draft.fechaTermino || draft.fechaTermino === draft.fechaInicio;
    if (soloPrecision || !valorCambio || !fechaInicio || !terminoLibre || draft.enCurso)
        return patch;
    return { ...patch, fechaTermino: fechaInicio, fechaTerminoPrecision: precision };
}
/** 'YYYY-MM-DD' de la fecha LOCAL (no UTC: `toISOString` adelanta el día de noche en Chile). */
function fechaLocalISO(fecha = new Date()) {
    const y = String(fecha.getFullYear()).padStart(4, '0');
    const m = String(fecha.getMonth() + 1).padStart(2, '0');
    const d = String(fecha.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}
/**
 * Techo de la fecha de término: 31 de diciembre de (año actual + 5). El término puede ser una
 * fecha PREVISTA (fin de un doctorado en curso, de un cargo con plazo).
 */
function fechaTerminoMaxima(hoy = new Date()) {
    return `${hoy.getFullYear() + 5}-12-31`;
}
/**
 * Límites de las fechas del CV: inicio ≤ hoy y término ≤ `fechaTerminoMaxima`. Con precisión
 * 'mes' la comparación es a nivel de mes (el día 01 guardado no es dato). Un ítem "en curso"
 * no valida el término. Devuelve los mensajes por campo (vacío si todo está en rango).
 * Solo lo usa el formulario: la API no lo exige, para no rechazar datos ya cargados.
 */
function erroresLimiteFechas(data, hoy = new Date()) {
    const errores = {};
    const excede = (fecha, precision, max) => precision === 'mes' ? fecha.slice(0, 7) > max.slice(0, 7) : fecha.slice(0, 10) > max;
    if (data.fechaInicio && excede(data.fechaInicio, data.fechaInicioPrecision, fechaLocalISO(hoy))) {
        errores.fechaInicio = 'No puede ser posterior a hoy';
    }
    const maxTermino = fechaTerminoMaxima(hoy);
    if (!data.enCurso && data.fechaTermino && excede(data.fechaTermino, data.fechaTerminoPrecision, maxTermino)) {
        errores.fechaTermino = `No puede ser posterior a ${maxTermino.slice(0, 4)}`;
    }
    return errores;
}
